/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Link, useFormatter, useRouter, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { Product } from "@packages/sdk";
import {
  Button,
  colors,
  IconButton,
  MinusIcon,
  numericPixels,
  PlusIcon,
  PurplePresentIcon,
  radius,
  spacing,
  Text,
} from "@packages/ui";
import { useState } from "react";

const baseStyles: Interpolation = {
  backgroundColor: colors.green70,
  borderRadius: radius.l,
  color: colors.white,
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  padding: spacing.xl,
  ":first-child": {
    backgroundColor: colors.purple40,
  },
};

const imageStyles: Interpolation = {
  maxWidth: numericPixels[100],
};

const actionsStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  marginTop: spacing.xl,
};

const quantityStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  textAlign: "center",
};

const counterStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.m,
};

const SCREEN_NAME = "gift_card";

export const GiftCardOption = ({ product }: { product: Product }) => {
  const t = useTranslations();
  const router = useRouter();
  const format = useFormatter();
  const analytics = useAnalytics();
  const [count, setCount] = useState(1);

  const nextStep = () => {
    analytics.track({
      event: HallowAnalyticsEvent.TappedBuyGift,
      properties: { screen_name: SCREEN_NAME },
    });
    router.push(`/gift/checkout?priceId=${product.price.id}&quantity=${count}`);
  };

  return (
    <section css={baseStyles}>
      <PurplePresentIcon css={imageStyles} />
      <Text size="l" type="headline">
        {product.name}
      </Text>
      <Text size="xl" type="title">
        {format.number(product.price.amount, {
          style: "currency",
          currency: product.price.currency,
        })}
      </Text>
      <Text size="m" type="body">
        {product.description}
      </Text>
      <div css={actionsStyles}>
        <div css={quantityStyles}>
          <Text size="l" type="detail">
            {t("general_word_quantity")}
          </Text>
          <div css={counterStyles}>
            <IconButton
              variant="lighten"
              icon={<MinusIcon.Simple />}
              onClick={() => setCount(count - 1)}
              disabled={count <= 1}
            />
            <Text size="m" type="title">
              {count}
            </Text>
            <IconButton
              variant="lighten"
              icon={<PlusIcon.Simple />}
              onClick={() => setCount(count + 1)}
              disabled={count >= 25}
            />
          </div>
        </div>

        <Button size="l" variant="neutral" onClick={() => nextStep()}>
          <Link
            href={`/gift/checkout?priceId=${product.price.id}&quantity=${count}`}
          >
            {t("gift_buy_button_text")}
          </Link>
        </Button>
      </div>
    </section>
  );
};
